import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, useTheme } from '@mui/material';

import CreateItem from './CreateItem';
import AssignCosts from './AssignCosts';

import CreateAttribute from './attributes/CreateAttribute';
import { useStore } from 'zustand';
import { a11yProps, CustomTabPanel } from '../../../customTabs/CustomTabPanel';
import { useFetchItemsByFileId } from '../../../hooks/useFetchItemsByFileId';
import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { getContrastTextColor } from '../../../utils/getCintrastTextColor';
import { HelpBubble } from '../../common/helpBubbles';
import { useFetchAttributeGroups } from '../../../hooks/useFetchAttributeGroups';

function CreateItemTab() {
  //zustand states
  const theme = useTheme();
  const { currentItem } = useStore(useGlobalItemStore);

  //react-query queries
  const { data: items } = useFetchItemsByFileId(currentItem?.itemid || '');
  const { data: attributeGroups } = useFetchAttributeGroups();

  //local state
  const [value, setValue] = React.useState(0);
  const [helpBubbleCoordinates, setHelpBubbleCoordinates] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [helpBubbleMessage, setHelpBubbleMessage] = React.useState('Create Attributes to begin');
  const tabsContainerRef = React.useRef<HTMLDivElement>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Calculate the position of the help bubble based on the current tab
  React.useEffect(() => {
    if (!attributeGroups || attributeGroups.length > 0) {
      setHelpBubbleCoordinates(null);
      return;
    }

    if (tabsContainerRef.current) {
      // Get the tabs container element
      const tabsContainer = tabsContainerRef.current;
      // Get all tab elements
      const tabs = tabsContainer.querySelectorAll('[role="tab"]');

      if (value === 2) {
        // On Item Attributes tab
        // Wait a bit for the tab content to render
        setTimeout(() => {
          // Find the Add button in the CreateAttribute component
          const addButtons = Array.from(document.querySelectorAll('button'));
          const addButton = addButtons.find(
            btn =>
              btn.textContent === 'Add' ||
              btn.textContent?.includes('Add') ||
              btn.getAttribute('aria-label')?.includes('Add attribute group')
          );

          if (addButton) {
            const rect = addButton.getBoundingClientRect();
            setHelpBubbleCoordinates({
              x: rect.left - 150,
              y: rect.bottom,
            });
            setHelpBubbleMessage('Give the Attribute a name and assign it a division then ADD');
          } else if (tabs.length > 2) {
            // Fallback to positioning below the tab
            const attributesTab = tabs[2];
            const rect = attributesTab.getBoundingClientRect();
            setHelpBubbleCoordinates({
              x: rect.left - 55,
              y: rect.bottom,
            });
            setHelpBubbleMessage('Create Attributes to begin');
          }
        }, 500);
      } else if (tabs.length > 2) {
        // The attributes tab is the third tab (index 2)
        const attributesTab = tabs[2];
        const rect = attributesTab.getBoundingClientRect();

        setHelpBubbleCoordinates({
          x: rect.left - 55,
          y: rect.bottom,
        });
        setHelpBubbleMessage('Create Attributes to begin');
      }
    } else {
      setHelpBubbleCoordinates(null);
    }
  }, [attributeGroups, value]);

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        color: getContrastTextColor(theme.palette.background.paper),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons
        aria-label="Item creation tabs"
        ref={tabsContainerRef}
        sx={{
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            color: getContrastTextColor(theme.palette.background.paper),
            textTransform: 'none',
            fontWeight: 500,
            minHeight: '48px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&.Mui-selected': {
              color: getContrastTextColor(theme.palette.background.paper),
              fontWeight: 600,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: getContrastTextColor(theme.palette.background.paper),
          },
        }}
      >
        <Tab
          label="Item Details"
          {...a11yProps(0)}
          aria-label="Item details tab"
          title="Item details"
        />
        <Tab
          label="Item Costs"
          {...a11yProps(1)}
          aria-label="Item costs tab"
          title="Item costs"
          disabled={!currentItem}
        />
        <Tab
          label="Item Attributes"
          {...a11yProps(2)}
          aria-label="Item attributes tab"
          title="Item attributes"
        />
      </Tabs>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          color: getContrastTextColor(theme.palette.background.paper),
        }}
      >
        <CustomTabPanel value={value} index={0}>
          <Box sx={{ maxHeight: '100%', overflow: 'auto', padding: '16px' }}>
            <CreateItem items={items || []} />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            <AssignCosts itemId={currentItem?.itemid || ''} />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <Box sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
            <CreateAttribute />
          </Box>
        </CustomTabPanel>
      </Box>

      {helpBubbleCoordinates && attributeGroups && attributeGroups.length === 0 && (
        <HelpBubble message={helpBubbleMessage} coordinates={helpBubbleCoordinates} />
      )}
    </Box>
  );
}

export default React.memo(CreateItemTab);
